<script setup lang="ts">
import { computed, watch } from 'vue'

import { PopoverConfirm } from '@collector/shared-ui'
import { Confirmation, Participant } from '@collector/sportsapi-client-legacy'
import {
  useEvent,
  useParticipantResultsMaps,
  useParticipants,
} from '@desktop/views/Relation/composables'
import IncidentButton from '@desktop/views/Relation/Sports/components/RelationView/IncidentButton.vue'
import {
  AddIncidentPayloadWithOptionalTime,
  useEventIncidentsQueue,
  usePitchIncidentsBlockades,
} from '@desktop/views/Relation/Sports/composables'
import {
  BlockingStrategy,
  ConfirmingStrategy,
  IncidentButtonData,
} from '@desktop/views/Relation/Sports/configuration'
import {
  injectIncidentsRequireConfirmation,
  injectResultWithSliderConfig,
} from '@desktop/views/Relation/Sports/provide-inject'

import {
  useAssignIncidentOnPitch,
  UseAssignIncidentOnPitch,
} from '../IncidentsOverlay/useAssignIncidentOnPitch'
import { useCanAddOneTbd } from './useCanAddOneTbd'

type IncidentButtonProps = InstanceType<typeof IncidentButton>['$props']

type Props = {
  participant?: Participant
  onClick?: (
    addIncident: () => void,
    setIncidentOpts?: {
      incidentId: IncidentButtonData['incidentId']
      participantTeamId: number
      setIncident: UseAssignIncidentOnPitch['setIncident']
    },
  ) => void
  buttonHeightClass?: string
  variant?: IncidentButtonProps['variant']
  disabled?: IncidentButtonProps['disabled']
  coordinates?: {
    x: number
    y: number
  }
} & Omit<IncidentButtonData, 'class'>

const props = defineProps<Props>()

// composables
const { setIncident } = useAssignIncidentOnPitch()
const { isEveryPitchIncidentBlocked, isPitchIncidentBlocked } =
  usePitchIncidentsBlockades()
const eventIncidentsQueue = useEventIncidentsQueue()
const { homeParticipant, awayParticipant } = useParticipants()
const { homeParticipantResultsMap, awayParticipantResultsMap } =
  useParticipantResultsMaps()
const {
  disabled: disabledOneTbd,
  disabledTooltipText,
  setUuid,
  setParticipantId,
} = props.canAddOneTbd && props.participant
  ? useCanAddOneTbd(props.name, props.incidentId, props.participant.id)
  : {}

const { event } = useEvent()

// inject
const incidentsRequireConfirmation = injectIncidentsRequireConfirmation()
const resultWithSliderConfig = injectResultWithSliderConfig()

function isButtonBlocked(strategy: BlockingStrategy): boolean {
  const resultId = currentResultId.value || null

  const home = homeParticipant.value
  const away = awayParticipant.value
  if (!home || !away) return false

  return strategy.isBlocked({
    homeResult: Number(
      resultId === null
        ? null
        : homeParticipantResultsMap.value[resultId]?.value,
    ),
    homeParticipantId: home.id,
    awayResult: Number(
      resultId === null
        ? null
        : awayParticipantResultsMap.value[resultId]?.value,
    ),
    awayParticipantId: away.id,
    currentParticipant: props.participant,
  })
}

function shouldBlockIncident(): boolean {
  return (
    isPitchIncidentBlocked(props.incidentId) ||
    (!!props.blockingStrategy && isButtonBlocked(props.blockingStrategy))
  )
}

// computed
const currentResultId = computed(() =>
  resultWithSliderConfig?.eventStatusToResultIdMap.get(event.value.status_id),
)

const isDisabled = computed(() => {
  // Individual conditions for readability
  const isExplicitlyDisabled = props.disabled
  const isOneTbdDisabled = disabledOneTbd?.value
  const isIncidentBlocked = shouldBlockIncident()

  // If all pitch incidents are blocked, an overlay that disables all buttons should be shown,
  // so this component doesn't need to be disabled separately which will avoid an extra layer of visual disabling.

  return (
    (isExplicitlyDisabled || isOneTbdDisabled || isIncidentBlocked) &&
    !isEveryPitchIncidentBlocked.value
  )
})

function addIncident(): string | void {
  const payload: AddIncidentPayloadWithOptionalTime = {
    incident_id: props.incidentId,
    ...(props.participant && { participant_team_id: props.participant.id }),
  }

  if (incidentsRequireConfirmation?.includes(props.incidentId)) {
    payload.confirmation = Confirmation.ToBeDone
  }

  if (props.attributeId !== undefined) {
    payload.attribute_id = [props.attributeId]
  }

  if (props.coordinates) {
    payload.x_pos = props.coordinates.x
    payload.y_pos = props.coordinates.y
  }

  // eslint-disable-next-line no-console
  console.debug(
    'PitchIncidentButton addIncident',
    JSON.stringify(payload),
    +new Date(),
  )

  const uuid = eventIncidentsQueue.addIncident(payload, {
    shouldResetPossession: true,
  })

  if (setUuid) {
    if (typeof uuid !== 'string') {
      throw new Error('uuid must be a string')
    }
    setUuid(uuid)
  }

  return uuid
}

function getIncidentNameParts(incidentName: string): string[] {
  const name = props.name || incidentName

  return props.breakLine ? name.split(' ') : [name]
}

function onButtonClick(): void {
  if (props.onClick) {
    // `onClick` function instead of emitting 'click'
    // allows to detect if parent component wants to handle the click with custom code (and call `addIncident` if needed)
    props.onClick(addIncident, {
      incidentId: props.incidentId,
      participantTeamId: props.participant?.id || 0,
      setIncident,
    })
  } else {
    addIncident()
  }
}

function isConfirmationRequired(): boolean {
  if (props.confirmingStrategy) {
    return true
  }
  return false
}

function getConfirmationDescription(): string {
  if (props.confirmingStrategy) {
    return getMatchResultConfirmation(props.confirmingStrategy)
  }
  return ''
}

function getUnblockInstructions(): string {
  if (props.blockingStrategy) {
    return isButtonBlocked(props.blockingStrategy)
      ? props.blockingStrategy.unblockInstructions
      : ''
  }
  return ''
}

function getMatchResultConfirmation(strategy: ConfirmingStrategy): string {
  return strategy.getConfirmationDescription(undefined, props.participant)
}

// init
watch(
  () => props.participant?.id,
  (newValue) => {
    if (setParticipantId && newValue) {
      setParticipantId(newValue)
    }
  },
)
</script>

<template>
  <PopoverConfirm
    v-if="isConfirmationRequired()"
    cancelLabel="No"
    confirmLabel="Yes"
    @confirm="() => onButtonClick()"
  >
    <template #confirmText>
      <div class="w-30">
        <div>
          {{ getConfirmationDescription() }}
        </div>
      </div>
    </template>
    <template #trigger>
      <IncidentButton
        class="h-full whitespace-pre-line"
        :class="buttonHeightClass ? buttonHeightClass : 'max-h-16 min-h-9'"
        :incidentId
        :withIcon
        :disabled="isDisabled"
        :tooltip="disabledTooltipText || getUnblockInstructions()"
        :variant
      >
        <template #default="{ incidentName }">
          <slot>
            <span class="text-2xs inline-flex flex-col px-1">
              <span
                v-for="(word, index) in getIncidentNameParts(incidentName)"
                :key="index"
              >
                {{ word }}
              </span>
            </span>
          </slot>
        </template>
      </IncidentButton>
    </template>
  </PopoverConfirm>
  <IncidentButton
    v-else
    class="h-full whitespace-pre-line"
    :class="buttonHeightClass ? buttonHeightClass : 'max-h-16 min-h-9'"
    :incidentId
    :withIcon
    :disabled="isDisabled"
    :tooltip="disabledTooltipText || getUnblockInstructions()"
    :variant
    @click="onButtonClick"
  >
    <template #default="{ incidentName }">
      <slot>
        <span class="text-2xs inline-flex flex-col px-1">
          <span
            v-for="(word, index) in getIncidentNameParts(incidentName)"
            :key="index"
          >
            {{ word }}
          </span>
        </span>
      </slot>
    </template>
  </IncidentButton>
</template>
